import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Shows announcements published at admin.isman.co.ke/announcements.
 * banner = full-width bar under the menu, inline = card, modal = popup (once per visit).
 * Visitors can close them; closed ones stay closed for this visit.
 */
interface Ann {
  id: number; title: string; body: string; type: "announcement" | "promotion" | "alert";
  display_style: "banner" | "modal" | "inline"; pages: "store" | "all" | "home";
  link_url: string; link_label: string;
}

const STYLE: Record<Ann["type"], { bar: string; icon: string; btn: string }> = {
  announcement: { bar: "bg-teal-700 text-white", icon: "ri-megaphone-line", btn: "bg-white text-teal-800 hover:bg-teal-50" },
  promotion: { bar: "bg-gradient-to-r from-amber-500 to-orange-600 text-white", icon: "ri-price-tag-3-line", btn: "bg-white text-orange-700 hover:bg-orange-50" },
  alert: { bar: "bg-rose-600 text-white", icon: "ri-error-warning-line", btn: "bg-white text-rose-700 hover:bg-rose-50" },
};

let cache: Ann[] | null = null;
let inflight: Promise<Ann[]> | null = null;
function loadAnnouncements(): Promise<Ann[]> {
  if (cache) return Promise.resolve(cache);
  inflight ||= fetch("/api/store/announcements.php", { cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : { items: [] }))
    .then((j) => (cache = Array.isArray(j.items) ? j.items : []))
    .catch(() => (cache = []));
  return inflight;
}
const closedKey = "isman_ann_closed";
const getClosed = (): number[] => { try { return JSON.parse(sessionStorage.getItem(closedKey) || "[]"); } catch { return []; } };

function Cta({ a, cls }: { a: Ann; cls: string }) {
  if (!a.link_url) return null;
  const ext = /^https?:\/\//.test(a.link_url) && !a.link_url.includes("isman.co.ke");
  return (
    <a href={a.link_url} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined}
      className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${cls}`}>
      {a.link_label || "Learn more"} <i className="ri-arrow-right-line"></i>
    </a>
  );
}

export default function AnnouncementBanners({ page, placement = "banner" }: { page?: "store" | "home"; placement?: "banner" | "inline" }) {
  const { pathname } = useLocation();
  const [items, setItems] = useState<Ann[]>([]);
  const [closed, setClosed] = useState<number[]>(getClosed);

  useEffect(() => { loadAnnouncements().then(setItems); }, []);
  // sit the banner just under the fixed site menu
  const [navH, setNavH] = useState(96);
  useEffect(() => {
    const m = () => { const h = document.querySelector("header")?.getBoundingClientRect().height; if (h) setNavH(Math.round(h)); };
    m(); addEventListener("resize", m); const t = setTimeout(m, 600);
    return () => { removeEventListener("resize", m); clearTimeout(t); };
  }, []);

  const where = page ?? (pathname.startsWith("/store") ? "store" : pathname === "/" ? "home" : "other");
  const visible = items.filter((a) => (a.pages === "all" || a.pages === where) && !closed.includes(a.id));
  const close = (id: number) => { const n = [...closed, id]; setClosed(n); try { sessionStorage.setItem(closedKey, JSON.stringify(n)); } catch { /* ignore */ } };

  if (placement === "inline") {
    const list = visible.filter((a) => a.display_style === "inline");
    if (!list.length) return null;
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 space-y-3">
        {list.map((a) => {
          const st = STYLE[a.type] || STYLE.announcement;
          return (
            <div key={a.id} className={`relative rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm ${st.bar}`}>
              <i className={`${st.icon} text-2xl opacity-90`}></i>
              <div className="flex-1 min-w-0"><p className="font-bold">{a.title}</p>{a.body && <p className="text-sm opacity-90">{a.body}</p>}</div>
              <Cta a={a} cls={st.btn} />
              <button onClick={() => close(a.id)} aria-label="Close" className="absolute top-2 right-3 opacity-70 hover:opacity-100"><i className="ri-close-line"></i></button>
            </div>
          );
        })}
      </div>
    );
  }

  const banners = visible.filter((a) => a.display_style === "banner");
  const modal = visible.find((a) => a.display_style === "modal");
  return (
    <>
      {banners.length > 0 && (
        <div className="relative z-40" style={{ paddingTop: navH, marginBottom: -navH }}>
          {banners.map((a) => {
            const st = STYLE[a.type] || STYLE.announcement;
            return (
              <div key={a.id} className={`${st.bar}`}>
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center gap-3 text-sm">
                  <i className={`${st.icon} text-lg shrink-0`}></i>
                  <p className="flex-1 min-w-0"><b>{a.title}</b>{a.body && <span className="opacity-90"> — {a.body}</span>}</p>
                  <Cta a={a} cls={st.btn} />
                  <button onClick={() => close(a.id)} aria-label="Close announcement" className="opacity-75 hover:opacity-100 text-lg shrink-0"><i className="ri-close-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => close(modal.id)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className={`px-6 py-5 ${(STYLE[modal.type] || STYLE.announcement).bar}`}>
              <i className={`${(STYLE[modal.type] || STYLE.announcement).icon} text-3xl`}></i>
              <h3 className="text-xl font-extrabold mt-2">{modal.title}</h3>
            </div>
            <div className="px-6 py-5">
              {modal.body && <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{modal.body}</p>}
              <div className="flex gap-2 mt-5 justify-end">
                <button onClick={() => close(modal.id)} className="text-sm text-gray-500 px-4 py-2">Close</button>
                {modal.link_url && <a href={modal.link_url} onClick={() => close(modal.id)} className="text-sm bg-teal-600 hover:bg-teal-500 text-white font-semibold px-4 py-2 rounded-lg">{modal.link_label || "Learn more"}</a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
